import { Control, Grouping, Menu } from '@yleisradio/areena-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Container } from 'components/Container';
import styles from './AreenaMenu.module.scss';
import { MenuBarItem } from './MenuBarItem';
import { SubMenu } from './SubMenu';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { serviceFromClientPath } from 'utils/url';

const FIXED_MENUBAR_GROUPS = 3;

interface MenuProps {
  menus: Menu[];
}

const menuBarContent = (groupings: Grouping[]) =>
  groupings.reduce(
    (acc: Control[], grouping) =>
      acc.concat(grouping.groupings, grouping.controls),
    []
  );

export const AreenaMenu: React.FunctionComponent<MenuProps> = ({ menus }) => {
  const { asPath: currentPath } = useRouter();

  const mainMenu = menus.find((menu) => menu.tag === 'main');
  const mainMenuGroupings = (mainMenu && mainMenu.groupings) || [];

  const personalMenu = menus.find((menu) => menu.tag === 'personal');
  const personalMenuGroupings = (personalMenu && personalMenu.groupings) || [];

  const trailingMenuBarGroupings =
    mainMenuGroupings.slice(FIXED_MENUBAR_GROUPS);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeSubMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    closeSubMenu();
  }, [currentPath]);

  useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    window.document.addEventListener('keydown', keydownHandler, true);

    return () =>
      window.document.removeEventListener('keydown', keydownHandler, true);
  }, [setIsMenuOpen]);

  const toggleSubMenu = () => setIsMenuOpen((subMenuOpen) => !subMenuOpen);

  const subMenus = mainMenuGroupings.flatMap(
    (menuBarGroup) => menuBarGroup.groupings
  );

  const { areenaService } = useAreenaService();
  const service = areenaService ?? serviceFromClientPath(currentPath);
  return (
    <nav className={styles.areenaMenu}>
      <Container>
        <div className={styles.menuWrapper}>
          <ul className={classNames(styles.menuBar, styles.mainMenubar)}>
            {menuBarContent(mainMenuGroupings).map((item) => (
              <MenuBarItem
                key={item.title}
                control={item}
                toggleSubMenu={toggleSubMenu}
                isMenuOpen={isMenuOpen}
                closeSubMenu={closeSubMenu}
              />
            ))}
          </ul>
          <ul className={classNames(styles.menuBar, styles.personalMenubar)}>
            {menuBarContent(personalMenuGroupings).map((item) => (
              <MenuBarItem
                key={item.title}
                control={item}
                toggleSubMenu={toggleSubMenu}
                isMenuOpen={isMenuOpen}
                closeSubMenu={closeSubMenu}
              />
            ))}
          </ul>
        </div>
      </Container>
      {isMenuOpen && (
        <SubMenu
          currentPath={currentPath}
          personalMenu={menuBarContent(personalMenuGroupings)}
          trailingMenus={menuBarContent(trailingMenuBarGroupings)}
          subMenus={subMenus}
          service={service}
          toggleSubMenu={toggleSubMenu}
        />
      )}
    </nav>
  );
};
